import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { backdrop, panel, tap } from "../motion";
import { IconX, IconChevronLeft, IconChevronRight } from "./icons";
import { Select } from "./Select";
import { RESIDENT_SUBSIDIES, RESIDENT_SUBSIDY_LABELS, useCreateAdmission, type ResidentSubsidy } from "../hooks/useResidents";
import { useHomes } from "../hooks/useSettings";
import { hkToday } from "../lib/dates";
import { fmtCell, MONTHS, monthCells, WEEKDAYS } from "./calendar-grid";

// Manual admission — same checklist generation as the WhatsApp handover import
// (POST /api/residents spawns the built admission to-do set server-side).

export function AdmissionForm({ open, defaultHomeId, onClose }: { open: boolean; defaultHomeId: string; onClose: () => void }) {
  const { data: homes } = useHomes();
  const create = useCreateAdmission();
  const [homeId, setHomeId] = useState(defaultHomeId);
  const [name, setName] = useState("");
  const [bed, setBed] = useState("");
  const [subsidy, setSubsidy] = useState<ResidentSubsidy | "">("");
  const [admissionDate, setAdmissionDate] = useState(hkToday());
  const [calView, setCalView] = useState(() => {
    const [y, m] = hkToday().split("-").map(Number);
    return { year: y!, month: m! - 1 };
  });

  // Reset the form each time it opens.
  useEffect(() => {
    if (open) {
      setHomeId(defaultHomeId);
      setName("");
      setBed("");
      setSubsidy("");
      setAdmissionDate(hkToday());
      const [y, m] = hkToday().split("-").map(Number);
      setCalView({ year: y!, month: m! - 1 });
      create.reset();
    }
  }, [open, defaultHomeId]);

  const canSubmit = homeId.length > 0 && name.trim().length > 0 && bed.trim().length > 0 && subsidy.length > 0 && admissionDate.length === 10;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  function submit() {
    if (subsidy === "") return; // guarded by canSubmit; keeps the mutation body typed
    create.mutate({ homeId, name, bed: bed.trim(), subsidy, admissionDate }, { onSuccess: onClose });
  }

  const cells = monthCells(calView.year, calView.month);
  const today = hkToday();
  const shiftMonth = (delta: number) =>
    setCalView((v) => {
      const month = v.month + delta;
      if (month < 0) return { year: v.year - 1, month: 11 };
      if (month > 11) return { year: v.year + 1, month: 0 };
      return { ...v, month };
    });
  const pickDate = (d: string) => {
    setAdmissionDate(d);
    const [y, m] = d.split("-").map(Number);
    setCalView({ year: y!, month: m! - 1 });
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div className="palette-backdrop" role="dialog" aria-modal="true" aria-label="New admission" onClick={onClose} variants={backdrop} initial="hidden" animate="show" exit="exit">
          <motion.div className="modal" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === "Enter" && canSubmit) submit(); }} variants={panel} initial="hidden" animate="show" exit="exit">
            <div className="modal-head">
              <h2>New admission</h2>
              <button type="button" className="btn btn-icon btn-ghost" aria-label="Close" onClick={onClose}><IconX /></button>
            </div>
            <div className="modal-body">
              <label htmlFor="adm-home">Home</label>
              <Select
                id="adm-home"
                value={homeId}
                onChange={setHomeId}
                placeholder="Choose a home…"
                options={[{ value: "", label: "Choose a home…", disabled: true }, ...(homes ?? []).map((h) => ({ value: h.id, label: h.name }))]}
              />
              <label htmlFor="adm-name">Name</label>
              <input id="adm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 黃小F" />
              <label htmlFor="adm-bed">Bed</label>
              <input id="adm-bed" value={bed} onChange={(e) => setBed(e.target.value)} placeholder="e.g. F19" />
              <label htmlFor="adm-subsidy">Subsidy type</label>
              <Select
                id="adm-subsidy"
                value={subsidy}
                onChange={(v) => setSubsidy(v as ResidentSubsidy)}
                placeholder="Choose a subsidy type…"
                options={[{ value: "", label: "Choose a subsidy type…", disabled: true }, ...RESIDENT_SUBSIDIES.map((s) => ({ value: s, label: RESIDENT_SUBSIDY_LABELS[s] }))]}
              />
              <label htmlFor="adm-date">Admission date</label>
              <div className="mini-cal is-inline" role="group" aria-label="Admission date">
                <div className="mini-cal-head">
                  <button type="button" className="mini-cal-nav" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
                    <IconChevronLeft />
                  </button>
                  <div className="mini-cal-title" aria-live="polite">
                    {MONTHS[calView.month]} {calView.year}
                  </div>
                  <button type="button" className="mini-cal-nav" aria-label="Next month" onClick={() => shiftMonth(1)}>
                    <IconChevronRight />
                  </button>
                </div>
                <div className="mini-cal-grid">
                  {WEEKDAYS.map((w) => (
                    <div key={w} className="mini-cal-dow">
                      {w}
                    </div>
                  ))}
                  {cells.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className={`mini-cal-day${c.out ? " out" : ""}${c.date === admissionDate ? " selected" : ""}${c.date === today ? " today" : ""}`}
                      aria-label={fmtCell(c.date)}
                      aria-pressed={c.date === admissionDate}
                      onClick={() => pickDate(c.date)}
                    >
                      {c.day}
                    </button>
                  ))}
                </div>
                <div className="mini-cal-foot">
                  <button type="button" className="mini-cal-today" onClick={() => pickDate(today)}>
                    Today
                  </button>
                </div>
              </div>
              {create.isError ? (
                <p className="error">{create.error instanceof Error && create.error.message ? create.error.message : "Admission failed — try again."}</p>
              ) : null}
            </div>
            <div className="modal-foot">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <motion.button type="button" className="btn btn-primary" {...tap} disabled={!canSubmit || create.isPending} onClick={submit}>
                {create.isPending ? "Adding…" : "Add resident"}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
